// frontend/src/pages/MyRentals.js
import React, { useEffect, useState } from 'react';
import { Link } from '../routing';
import api from '../utils/api';
import { format, isPast, formatDistanceToNow } from 'date-fns';
import { Sparkles, Clock, AlertCircle, CheckCircle, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../i18n';
import { PLACEHOLDER_IMG } from '../config';
import PageHero from '../components/PageHero';
import { Reveal } from '../components/Motion';
import theme from '../theme';

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';

export default function MyRentals() {
  const { t, i18n } = useTranslation();
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  // "You have no rentals" and "we could not load your rentals" are very different
  // messages to show someone with an active rental and a deposit on the line.
  const [loadError, setLoadError] = useState(false);
  // Which rentals have a receipt waiting. One call answers it for the whole
  // list; the document itself is opened from the rental's own page, because
  // fetching it needs the bearer token and a card in a list is the wrong place
  // to be juggling that.
  const [invoicedRentals, setInvoicedRentals] = useState(() => new Set());

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = FONTS;
    document.head.appendChild(link);
    api.get('/rentals/my')
      .then(setRentals)
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
    // Silent on failure by design: a missing receipt marker is a cosmetic loss,
    // and an error about it would sit on top of rentals that are perfectly fine.
    api.get('/invoices/mine')
      .then(list => setInvoicedRentals(new Set((Array.isArray(list) ? list : []).map(d => d.rental_id).filter(Boolean))))
      .catch(() => {});
    return () => document.head.removeChild(link);
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: theme.cream, fontFamily: theme.font.body }}>
      <PageHero title={t('myRentals.title')} subtitle={t('myRentals.subtitle')} height={260} />
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '48px 24px 80px' }}>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 80, color: theme.gold, fontFamily: theme.font.display, fontSize: 24 }}>{t('myRentals.loading')}</div>
        ) : loadError ? (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <h2 style={{ fontFamily: theme.font.display, fontSize: 28, color: theme.ink, marginBottom: 8 }}>{t('myRentals.loadErrorTitle')}</h2>
            <p style={{ color: theme.grey400, marginBottom: 32 }}>{t('myRentals.loadErrorSubtitle')}</p>
            <button
              onClick={() => window.location.reload()}
              style={{ background: theme.ink, color: theme.white, padding: '14px 40px', borderRadius: 2, border: 'none', fontSize: 13, letterSpacing: '0.1em', cursor: 'pointer', fontFamily: theme.font.body }}>
              {t('myRentals.retry')}
            </button>
          </div>
        ) : rentals.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✨</div>
            <h2 style={{ fontFamily: theme.font.display, fontSize: 28, color: theme.ink, marginBottom: 8 }}>{t('myRentals.noRentalsTitle')}</h2>
            <p style={{ color: theme.grey400, marginBottom: 32 }}>{t('myRentals.noRentalsSubtitle')}</p>
            <Link to="/browse" style={{ background: theme.ink, color: theme.white, padding: '14px 40px', borderRadius: 2, textDecoration: 'none', fontSize: 13, letterSpacing: '0.1em' }}>
              {t('myRentals.browseBtn')}
            </Link>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {rentals.map((rental, i) => (
              <Reveal key={rental.id} delay={Math.min(i, 5) * 0.07} y={22}>
                <RentalCard rental={rental} hasDocument={invoicedRentals.has(rental.id)} />
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RentalCard({ rental, hasDocument }) {
  const { t, i18n } = useTranslation();
  const dateLocale = getDateLocale();

  const STATUS_CONFIG = {
    pending_payment: { label: t('myRentals.statusPendingPayment'), ...theme.state.pending, icon: <Clock size={14} /> },
    paid:            { label: t('myRentals.statusPaid'), ...theme.state.success, icon: <CheckCircle size={14} /> },
    picked_up:       { label: t('myRentals.statusPickedUp'), ...theme.state.info, icon: <Sparkles size={14} /> },
    returned:        { label: t('myRentals.statusReturned'), ...theme.state.neutral, icon: <CheckCircle size={14} /> },
    late:            { label: t('myRentals.statusLate'), ...theme.state.danger, icon: <AlertCircle size={14} /> },
    cleaning:        { label: t('myRentals.statusCleaning'), ...theme.state.neutral, icon: <Clock size={14} /> },
    cancelled:       { label: t('myRentals.statusCancelled'), ...theme.state.muted, icon: null },
    disputed:        { label: t('myRentals.statusDisputed'), ...theme.state.dispute, icon: <AlertCircle size={14} /> },
    lost:            { label: t('myRentals.statusLost'), ...theme.state.lost, icon: <AlertCircle size={14} /> },
  };

  const cfg = STATUS_CONFIG[rental.status] || STATUS_CONFIG.pending_payment;
  const img = rental.image_urls?.[0] || PLACEHOLDER_IMG;
  // Lateness only exists once the goods are actually out with the customer. The
  // late-fee cron charges only status IN ('picked_up','late'), so the badge must
  // match: due_at is stamped at PAYMENT and not rebased until collection, so a
  // paid-but-not-yet-collected rental (a normal locker no-show window — the
  // no-show cron even mails a fresh pickup code) would otherwise show a red
  // "OVERDUE by N days" for goods the customer has not received and is not being
  // charged for (follow-up audit F1). ('returned'/'cancelled'/'disputed' are
  // excluded for free by this allow-list.)
  const isLate = ['picked_up', 'late'].includes(rental.status) && rental.due_at && isPast(new Date(rental.due_at));

  return (
    <Link to={`/rental/${rental.id}`} style={{ textDecoration: 'none' }}>
      <div style={{
        background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4,
        overflow: 'hidden', display: 'flex', gap: 0,
        transition: 'box-shadow 0.2s', cursor: 'pointer'
      }}
        onMouseEnter={e => e.currentTarget.style.boxShadow = `0 8px 32px ${theme.alpha.black(0.1)}`}
        onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
      >
        <img src={img} alt={rental.product_name} style={{ width: 120, objectFit: 'cover', flexShrink: 0 }} />
        <div style={{ padding: 24, flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ fontFamily: theme.font.display, fontSize: 22, color: theme.ink, margin: 0 }}>{rental.product_name}</h3>
            {/* textTransform, not .toUpperCase(): the JS version strips the
                accents off the Greek labels (ΕΝΟΙΚΙΆΣΕΙΣ), which is a misspelling
                rather than a style. */}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: isLate ? theme.redWash : cfg.bg, color: isLate ? theme.redDark : cfg.color, padding: '4px 12px', borderRadius: 100, fontSize: 12, whiteSpace: 'nowrap', textTransform: 'uppercase' }}>
              {isLate ? <AlertCircle size={12} /> : cfg.icon}
              {isLate ? t('myRentals.overdue') : cfg.label}
            </span>
          </div>

          <div style={{ display: 'flex', gap: 24, marginTop: 12, color: theme.grey500, fontSize: 13, flexWrap: 'wrap' }}>
            <span>{rental.rental_days} {t('myRentals.days')}</span>
            <span>€{parseFloat(rental.total_rental_price).toFixed(2)} {t('myRentals.rental')}</span>
            {rental.due_at && (
              <span style={{ color: isLate ? theme.red : theme.grey500 }}>
                {isLate
                  ? t('myRentals.overdueBy', { time: formatDistanceToNow(new Date(rental.due_at), { locale: dateLocale }) })
                  : t('myRentals.due', { date: format(new Date(rental.due_at), 'MMM d, yyyy', { locale: dateLocale }) })}
              </span>
            )}
          </div>

          {hasDocument && (
            <div style={{ marginTop: 12, fontSize: 13, color: theme.goldInk, display: 'flex', alignItems: 'center', gap: 6 }}>
              <FileText size={14} /> {t('myRentals.documentAvailable')}
            </div>
          )}

          {rental.status === 'disputed' && (
            <div style={{ marginTop: 12, fontSize: 13, color: theme.purple, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertCircle size={14} /> {t('myRentals.disputeSignal')}
            </div>
          )}

          {rental.status === 'paid' && (
            <div style={{ marginTop: 12, fontSize: 13, color: theme.greenDeep, fontWeight: 500 }}>
              {rental.pickup_take_code
                ? t('myRentals.pickupCodeReady', { code: rental.pickup_take_code })
                : t('myRentals.pickupCodePending')}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
